import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { EscrowDealCard, DashboardDeadline, EscrowParty, CockpitParty } from '../../types';
import { api } from '../../lib/api';
import NudgeComposer from './NudgeComposer';
import CallInGhl, { UNREAD } from '../CallInGhl';
import { deadlineDate, pacificWallMs } from '../../utils/deadlineDates';

const fmtMoney = (v: number | null) =>
  v == null ? '-' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v);
const toUTC = (iso: string) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const fmtDate = (iso: string) => deadlineDate(iso) ?? '';

const ROLE_LABEL: Record<string, string> = { buyer: 'Buyer', seller: 'Seller', lender: 'Lender', escrow_officer: 'Escrow', title_officer: 'Title' };
// The design's status palette: green cleared, KW red overdue, amber next, gray for the rest.
const NODE_CLS: Record<string, string> = { emerald: 'border-success', rose: 'border-primary-500', amber: 'border-warning', gray: 'border-gray-300' };
const contingencyCls = (s: string) =>
  s === 'removed' ? 'bg-success-wash text-success' : s === 'overdue' ? 'bg-primary-100 text-primary-500' : 'bg-warning-wash text-warning';

interface Node { id: string; pct: number; color: string; label: string; date: string; next: boolean }

function buildTimeline(anchor: string | null, coe: string | null, dls: DashboardDeadline[], nowMs: number): { today: number; nodes: Node[] } | null {
  if (!anchor || !coe) return null;
  const a = toUTC(anchor), c = toUTC(coe), span = c - a;
  if (!(span > 0)) return null;
  const pct = (ms: number) => Math.max(0, Math.min(100, ((ms - a) / span) * 100));
  const dated = dls.filter((d) => d.due_date);
  const nextId = [...dated].filter((d) => !d.is_completed).sort((x, y) => toUTC(x.due_date!) - toUTC(y.due_date!))[0]?.id;
  const nodes = dated.map((d) => {
    let color = 'gray';
    if (d.is_completed) color = 'emerald';
    else if (d.days_until != null && d.days_until < 0) color = 'rose';
    else if (d.id === nextId) color = 'amber';
    return { id: d.id, pct: pct(toUTC(d.due_date!)), color, label: d.label, date: d.due_date!, next: d.id === nextId };
  });
  // ESC1-MARK: the marker stands on the PACIFIC day - the axis is UTC-midnight dates, Date.now() is tomorrow from 5 PM PT.
  return { today: pct(pacificWallMs(nowMs)), nodes };
}

const toCockpit = (p: EscrowParty): CockpitParty => ({
  role: p.role, name: p.name, contact_id: p.contact_id, company_id: null, email: p.email, phone: p.phone, ghl_linked: p.ghl_linked,
  ghl_link_unread: p.ghl_link_unread, can_add_to_ghl: p.can_add_to_ghl,
});

/**
 * PHONE1 (F1): a party row is keyed by the PERSON, never the position - the list can come back in another order, and an
 * index key would hand one person's row (and the Call page just added on it) to whoever sits there next. The role is in
 * the key because one contact can hold two roles on a deal (deal_contacts is unique on deal, contact AND role).
 */
const partyKey = (p: EscrowParty, i: number) => (p.contact_id ? `${p.contact_id}-${p.role}` : `${p.role}-${i}`);

export default function DealLane({ deal, deadlines, onChanged }: { deal: EscrowDealCard; deadlines: DashboardDeadline[]; onChanged: () => void }) {
  const navigate = useNavigate();
  const [composer, setComposer] = useState<{ party: CockpitParty; channel: 'sms' | 'email' } | null>(null);
  const [tasks, setTasks] = useState(deal.loose_tasks);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const topFlag = deal.risk.flags[0];
  const railCls = topFlag?.key === 'overdue' || topFlag?.key === 'emd_at_risk' ? 'bg-primary-500'
    : deal.risk.score > 0 ? 'bg-warning' : 'bg-success';
  const tl = buildTimeline(deal.escrow_opened_date, deal.milestones.coe_date, deadlines, Date.now());
  const next = deal.milestones.next;

  const openContact = (p: EscrowParty) => { if (p.contact_id) navigate(`/crm/contacts/${p.contact_id}`); };

  const toggleTask = async (t: { id: string; is_completed: boolean }) => {
    setTasks((ts) => ts.map((x) => x.id === t.id ? { ...x, is_completed: !x.is_completed } : x));
    try { await api.updateDealTask(deal.id, t.id, { is_completed: !t.is_completed }); } finally { onChanged(); }
  };
  const addTask = async () => {
    if (!newTitle.trim()) return;
    setAdding(false);
    const title = newTitle.trim(); setNewTitle('');
    await api.createDealTask(deal.id, { title });
    onChanged();
  };

  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className={`absolute inset-y-0 left-0 w-1 ${railCls}`} />
      {/* header */}
      <div className="flex items-center gap-3.5 p-4 pl-5">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gray-100 text-gray-400">
          {deal.property?.photo_url
            ? <img src={deal.property.photo_url} alt="" className="w-full h-full object-cover" />
            : <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21h18M4 21V8l8-5 8 5v13M9 21v-6h6v6" /></svg>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate(`/crm/deals/${deal.id}`)} className="truncate text-[14px] font-semibold text-gray-900 hover:text-primary-500">{deal.deal_name}</button>
            <span className="whitespace-nowrap rounded-full bg-info-wash px-2.5 py-1 text-[11px] font-semibold capitalize leading-none text-info">{deal.stage.replace(/_/g, ' ')}</span>
          </div>
          <p className="mt-0.5 truncate text-[12.5px] text-gray-500">
            {[deal.property?.address, deal.property?.city].filter(Boolean).join(', ') || 'No property linked'}
            {deal.property?.building_size ? ` · ${deal.property.building_size.toLocaleString()} sf` : ''}
          </p>
        </div>
        <div className="text-right whitespace-nowrap">
          <p className="font-display text-[19px] font-medium leading-none tabular-nums text-gray-900">{fmtMoney(deal.money.price)}</p>
          <p className="mt-1 text-[11.5px] text-gray-500">fee {fmtMoney(deal.money.commission)}{deal.money.days_to_close != null ? ` · closes ${deal.money.days_to_close}d` : ''}</p>
        </div>
      </div>

      {/* timeline */}
      {tl && (
        <div className="px-5 pb-1">
          <div className="relative h-10 mx-1">
            <div className="absolute top-2 left-0 right-0 h-[3px] bg-gray-200 rounded-full" />
            <div className={`absolute top-2 left-0 h-[3px] rounded-full ${deal.risk.score > 0 ? 'bg-warning' : 'bg-success'}`} style={{ width: `${tl.today}%` }} />
            {tl.nodes.map((n) => (
              <div key={n.id} title={`${n.label} · ${fmtDate(n.date)}`}
                className={`absolute rounded-full bg-white ${NODE_CLS[n.color]}`}
                style={{ top: n.next ? '2px' : '3px', left: `${n.pct}%`, transform: 'translateX(-50%)', width: n.next ? '15px' : '13px', height: n.next ? '15px' : '13px', borderWidth: n.next ? '3px' : '2px' }} />
            ))}
            <div className="absolute w-0.5 bg-gray-900" style={{ top: '-1px', height: '19px', left: `${tl.today}%`, transform: 'translateX(-50%)' }} title="Today" />
            {/* ⛔⛔ No date is ever hidden (ESC1-UI, looked at 2026-10-03). The marker lives in its own lane that ENDS
                where the COE caption begins - one flex row, so neither is drawn over the other (it ran 40 px under
                "COE Apr 21, 2027"). Its zone and widest extent are measured in that lane, so it never leaves it, and
                only the label truncates - the date is its own piece, so an ellipsis eats prose, never the date. */}
            <div className="absolute top-5 left-0 right-0 flex gap-3 whitespace-nowrap text-[10.5px]">
              <div className="relative min-w-0 flex-1">
                {next && (() => {
                  const p = tl.nodes.find((x) => x.next)?.pct ?? 50;
                  // Bounded by the lane alone: the old 55% cap only kept it off the COE caption, which it can no longer reach.
                  const [tf, widest] = p < 12 ? ['translateX(0)', 100 - p]
                    : p > 70 ? ['translateX(-100%)', p]
                    : ['translateX(-50%)', Math.min(2 * p, 2 * (100 - p))];
                  return (
                    <div className="absolute top-0 flex font-medium text-warning" style={{ left: `${p}%`, transform: tf, maxWidth: `${widest}%` }} title={`${next.label} · ${fmtDate(next.due_date)}`}>
                      <span className="min-w-0 truncate">{next.label}</span>
                      <span className="shrink-0">&nbsp;· {fmtDate(next.due_date)}</span>
                    </div>
                  );
                })()}
              </div>
              {deal.milestones.coe_date && (
                <div className="shrink-0 text-gray-500">COE {fmtDate(deal.milestones.coe_date)}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* contingency chips */}
      {deal.contingencies.length > 0 && (
        <div className="px-5 py-2.5 flex flex-wrap gap-1.5">
          {deal.contingencies.map((c) => (
            <span key={c.type} className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${contingencyCls(c.status)}`}>
              {c.label} {c.status === 'removed' ? 'cleared' : c.status === 'overdue' ? `${Math.abs(c.days_until ?? 0)}d overdue` : c.days_until != null ? `due ${c.days_until}d` : 'active'}
            </span>
          ))}
        </div>
      )}

      {/* parties */}
      {deal.parties_detailed.length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 space-y-2">
          <p className="text-[9.5px] font-bold uppercase tracking-[.13em] text-gray-400">Parties</p>
          {/* LF2: said ONCE here - inline in each row it pushed every party's buttons over the names on a phone. */}
          {deal.parties_detailed.some((p) => p.ghl_link_unread) && <p role="status" className="text-xs text-gray-500">{UNREAD}</p>}
          {deal.parties_detailed.map((p, i) => (
            <div key={partyKey(p, i)} className="flex items-center gap-3">
              <span className="text-[10.5px] text-gray-400 w-12 flex-shrink-0">{ROLE_LABEL[p.role] || p.role}</span>
              <div className="flex-1 min-w-0">
                {p.contact_id
                  ? <button onClick={() => openContact(p)} className="block truncate text-left text-[13px] font-medium text-primary-500 hover:underline underline-offset-[3px]">{p.name || 'Unnamed'}</button>
                  : <span className="text-[13px] text-gray-700 truncate block">{p.name || 'Unnamed'}</span>}
                <p className="text-[11.5px] text-gray-400 truncate">{[p.phone, p.email].filter(Boolean).join(' · ') || 'No contact info'}</p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <CallInGhl variant="icon" url={p.ghl_url} contactId={p.contact_id} canAdd={!!p.can_add_to_ghl} unread={p.ghl_link_unread} />
                <button onClick={() => setComposer({ party: toCockpit(p), channel: 'sms' })} disabled={!p.contact_id || !p.phone} title={p.phone ? 'Text' : 'No phone number'} className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-gray-900 hover:text-gray-900 disabled:opacity-40">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" /></svg>
                </button>
                <button onClick={() => setComposer({ party: toCockpit(p), channel: 'email' })} disabled={!p.contact_id || !p.email} title={p.email ? 'Email' : 'No email'} className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-gray-900 hover:text-gray-900 disabled:opacity-40">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2z" /><path d="M22 6l-10 7L2 6" /></svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* loose tasks */}
      <div className="px-5 py-3 border-t border-gray-100">
        <p className="mb-2 text-[9.5px] font-bold uppercase tracking-[.13em] text-gray-400">Tasks · not in checklist</p>
        {tasks.length === 0 && !adding && <p className="text-[13px] text-gray-400 mb-1">None yet.</p>}
        {tasks.map((t) => (
          <div key={t.id} className="flex items-center gap-2.5 mb-1.5">
            <input type="checkbox" checked={t.is_completed} onChange={() => toggleTask(t)} className="rounded-sm border-gray-300 text-primary-500 focus:ring-primary-500/25" />
            <span className={`flex-1 text-[13px] ${t.is_completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>{t.title}</span>
            {t.due_date && <span className="text-xs text-gray-400 tabular-nums">{fmtDate(t.due_date)}</span>}
          </div>
        ))}
        {adding ? (
          <div className="flex items-center gap-2 mt-1">
            <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addTask()} placeholder="Task title..." className="h-[30px] flex-1 rounded-lg border-gray-300 py-0 text-[12.5px] focus:border-primary-500 focus:ring-2 focus:ring-primary-500/25" />
            <button type="button" onClick={addTask} className="h-[30px] rounded-lg bg-primary-500 px-3 text-[11.5px] font-semibold text-white hover:bg-primary-600">Add</button>
            <button type="button" onClick={() => { setAdding(false); setNewTitle(''); }} className="text-[11.5px] text-gray-500 hover:text-gray-800">Cancel</button>
          </div>
        ) : (
          <button type="button" onClick={() => setAdding(true)} className="text-[11.5px] font-semibold text-primary-500 hover:underline underline-offset-[3px]">+ add task</button>
        )}
      </div>

      {composer && (
        <NudgeComposer dealId={deal.id} party={composer.party} channel={composer.channel} nextLabel={next?.label ?? null} nextDate={next?.due_date ?? null} onClose={() => setComposer(null)} />
      )}
    </div>
  );
}
